if (!customElements.get('product-form')) {
  const shippingTimingErrorMessage =
    'Items in your cart ship at different times. In-stock items and pre-order items with different release dates must be purchased separately so shipping is charged correctly.';
  const specialOrderErrorMessage =
    'Special-order items must be purchased separately. Your cart may contain only one special-order item type, though you can choose any quantity of that item.';

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errorMessage = this.getCartErrorMessage(response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: errorMessage,
                message: errorMessage,
              });
              this.handleErrorMessage(errorMessage);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'Could not add this item to cart.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      collectCartErrorMessages(value, messages = []) {
        if (!value) return messages;

        if (typeof value === 'string') {
          const cleanMessage = value.trim();
          if (cleanMessage) messages.push(cleanMessage);
          return messages;
        }

        if (Array.isArray(value)) {
          value.forEach((entry) => this.collectCartErrorMessages(entry, messages));
          return messages;
        }

        if (typeof value === 'object') {
          Object.values(value).forEach((entry) => this.collectCartErrorMessages(entry, messages));
        }

        return messages;
      }

      getCartErrorMessage(response) {
        const messages = this.collectCartErrorMessages([
          response.description,
          response.message,
          response.errors,
        ]);
        const specialOrderMessage = messages.find((message) => {
          const normalized = message.toLowerCase();
          return (
            normalized.includes('special-order') ||
            normalized.includes('special order') ||
            normalized.includes('only one special-order') ||
            normalized.includes('only one special order')
          );
        });
        const timingMessage = messages.find((message) => {
          const normalized = message.toLowerCase();
          return (
            normalized.includes('ship at different times') ||
            normalized.includes('different release dates') ||
            normalized.includes('shipping is charged correctly')
          );
        });

        if (specialOrderMessage) return specialOrderErrorMessage;
        if (timingMessage) return shippingTimingErrorMessage;
        return messages[0] || window.cartStrings?.error || 'Could not add this item to cart.';
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
